/**
 * Dynamic country-based address form configuration.
 * Ported from platform-next — originally from dynamic-location-form-poc.
 *
 * Three-tier data strategy:
 *   Tier 1: Static JSON bundle (17 known countries) — instant
 *   Tier 2: On-demand fetch from Google Address Data Service — ~200ms, cached
 *   Tier 3: country-region-data npm package (lazy-loaded fallback)
 *   Tier 4: Generic fallback — all fields as optional text inputs
 */

import { useEffect, useState } from "react";
import { CountryAddressConfig } from "./types";
import staticData from "./data/addressData.json";

type ConfigStatus = "ready" | "loading" | "fallback";

interface UseCountryAddressConfigResult {
  config: CountryAddressConfig;
  status: ConfigStatus;
}

const GENERIC_FALLBACK: CountryAddressConfig = {
  regionFieldType: "text",
  regionLabel: "State / Region",
  regionRequired: false,
  postalLabel: "Postal Code",
  postalRequired: false,
};

const tier1Data = staticData.countries as Record<string, CountryAddressConfig>;
const googleSupportedCountries = new Set(staticData.googleSupportedCountries as string[]);

const tier2Cache = new Map<string, CountryAddressConfig>();

function mapStateNameType(type?: string): string {
  const map: Record<string, string> = {
    state: "State",
    province: "Province",
    prefecture: "Prefecture",
    county: "County",
    parish: "Parish",
    island: "Island",
    district: "District",
    voivodeship: "Voivodeship",
  };
  return (type && map[type]) ?? "State / Region";
}

function mapZipNameType(type?: string): string {
  const map: Record<string, string> = {
    zip: "ZIP Code",
    postal: "Postal Code",
    pin: "PIN Code",
  };
  return (type && map[type]) ?? "Postal Code";
}

interface GoogleAddressData {
  require?: string;
  zip?: string;
  zip_name_type?: string;
  state_name_type?: string;
  sub_keys?: string;
  sub_names?: string;
}

function parseGoogleData(data: GoogleAddressData): CountryAddressConfig {
  const require = data.require ?? "";
  const hasSubKeys = !!data.sub_keys;
  const regionRequired = require.includes("S");
  const postalRequired = require.includes("Z");

  let regionFieldType: CountryAddressConfig["regionFieldType"];
  if (hasSubKeys && regionRequired) {
    regionFieldType = "dropdown";
  } else if (hasSubKeys || data.state_name_type) {
    regionFieldType = "text";
  } else {
    regionFieldType = "hidden";
  }

  let regionOptions: CountryAddressConfig["regionOptions"];
  if (regionFieldType === "dropdown" && data.sub_keys) {
    const codes = data.sub_keys.split("~");
    const names = data.sub_names?.split("~") ?? codes;
    regionOptions = codes.map((code, i) => ({
      code,
      name: names[i] ?? code,
    }));
  }

  return {
    regionFieldType,
    regionLabel: mapStateNameType(data.state_name_type),
    regionRequired,
    ...(regionOptions && { regionOptions }),
    postalLabel: mapZipNameType(data.zip_name_type),
    postalRequired,
    ...(data.zip && { postalRegex: data.zip }),
  };
}

async function fetchTier2(countryCode: string): Promise<CountryAddressConfig> {
  const url = `https://chromium-i18n.appspot.com/ssl-address/data/${countryCode}`;
  const res = await fetch(url, { redirect: "follow" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as GoogleAddressData;
  return parseGoogleData(data);
}

export function useCountryAddressConfig(countryCode: string): UseCountryAddressConfigResult {
  const [asyncConfig, setAsyncConfig] = useState<CountryAddressConfig | null>(null);
  const [status, setStatus] = useState<ConfigStatus>("ready");

  const tier1Config = countryCode ? tier1Data[countryCode] : undefined;

  useEffect(() => {
    if (!countryCode || tier1Config) {
      setAsyncConfig(null);
      setStatus("ready");
      return;
    }

    const cached = tier2Cache.get(countryCode);
    if (cached) {
      setAsyncConfig(cached);
      setStatus("ready");
      return;
    }

    let cancelled = false;
    const googleHasCountry = googleSupportedCountries.has(countryCode);

    if (googleHasCountry) {
      setStatus("loading");
    }

    (async () => {
      if (googleHasCountry) {
        try {
          const config = await fetchTier2(countryCode);
          tier2Cache.set(countryCode, config);
          if (!cancelled) {
            setAsyncConfig(config);
            setStatus("ready");
          }
          return;
        } catch {
          // Tier 2 failed, fall through to generic fallback
        }
      }

      // Note: platform-next includes a Tier 3 fallback using the country-region-data
      // npm package. CRA's webpack resolves dynamic imports at build time and fails
      // if the package isn't installed, so we skip Tier 3 here. Tier 1 (17 countries)
      // + Tier 2 (Google API) cover all operational countries. If a new country is
      // needed, add it to the Tier 1 static data (data/addressData.json).
      if (!cancelled) {
        setAsyncConfig(GENERIC_FALLBACK);
        setStatus("fallback");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [countryCode, tier1Config]);

  if (!countryCode) {
    return { config: GENERIC_FALLBACK, status: "ready" };
  }

  if (tier1Config) {
    return { config: tier1Config, status: "ready" };
  }

  return {
    config: asyncConfig ?? GENERIC_FALLBACK,
    status,
  };
}
